import { useMutation } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";
import { EventAttendanceReportDto } from "../../../models/event/dto/event-attendance-report.dto";
import { EventType } from "../../../models/event/dto/event.dto";

export type EventAttendanceReportParams = {
    locationId: string;
    /** Local calendar day, `YYYY-MM-DD`. */
    from: string;
    /** Local calendar day, `YYYY-MM-DD`. Inclusive. */
    to: string;
    /** Omit for every event type. */
    eventType?: EventType;
    /** Defaults to true — cancelled sessions belong in a frequency analysis. */
    includeCancelled?: boolean;
};

/**
 * Turns the two calendar days the operator picked into the exact instants the
 * report should cover, expressed in THEIR timezone. Sending the bare dates
 * would have the API interpret them as UTC days, which silently shifts a
 * 22:00 class into the neighbouring day for any venue west of Greenwich.
 */
export function toReportWindow(from: string, to: string) {
    const [fromYear, fromMonth, fromDay] = from.split('-').map(Number);
    const [toYear, toMonth, toDay] = to.split('-').map(Number);
    const start = new Date(fromYear, fromMonth - 1, fromDay, 0, 0, 0, 0);
    const end = new Date(toYear, toMonth - 1, toDay, 23, 59, 59, 999);
    return { start, end };
}

/**
 * Mirrors MAX_ATTENDANCE_REPORT_RANGE_DAYS in the API. Because the window the
 * API measures runs from local midnight to local end-of-day, this is 366
 * INCLUSIVE days — 366 whole days of sessions, not 366 days plus a day.
 */
export const MAX_REPORT_RANGE_DAYS = 366;

const DAY_MS = 24 * 60 * 60 * 1000;

export type ReportRangeError = 'dates_required' | 'range_inverted' | 'range_too_long';

/**
 * Validates the picked range against the SAME window the request will carry —
 * `toReportWindow`, not the bare date strings.
 *
 * Measuring the bare dates instead is off by the end-of-day the request adds:
 * a 367-day pick measures as 366 days locally, passes, and is then rejected by
 * the API as a 400 the operator can only read as "the export is broken". The
 * two must be computed the same way, which is why this lives next to
 * `toReportWindow` rather than in the component.
 */
export function validateReportRange(from: string, to: string): ReportRangeError | undefined {
    if (!from || !to) return 'dates_required';
    const { start, end } = toReportWindow(from, to);
    const span = end.getTime() - start.getTime();
    if (Number.isNaN(span)) return 'dates_required';
    if (span < 0) return 'range_inverted';
    if (span > MAX_REPORT_RANGE_DAYS * DAY_MS) return 'range_too_long';
    return undefined;
}

/**
 * Fetches the group-class occupancy report on demand.
 *
 * A mutation rather than a query on purpose: this runs when the operator
 * presses Export, with a window they just chose, and its result is written to
 * a file rather than rendered — there is nothing to cache or re-fetch.
 */
export const useEventAttendanceReport = () => {
    const client = useApiClient();

    return useMutation({
        mutationFn: ({
            locationId,
            from,
            to,
            eventType,
            includeCancelled = true,
        }: EventAttendanceReportParams) => {
            const { start, end } = toReportWindow(from, to);
            const params = new URLSearchParams({
                locationId,
                from: start.toISOString(),
                to: end.toISOString(),
                includeCancelled: String(includeCancelled),
            });
            if (eventType) params.set('eventType', eventType);

            return client.fetchApi<EventAttendanceReportDto, {}>({
                endpoint: `events/reports/attendance?${params.toString()}`,
                method: 'GET',
            });
        },
    });
};
